/**
 * Lesson API Functions and Hooks
 * Requirements: 4.2, 4.5
 *
 * Provides TanStack Query hooks for fetching and saving lesson data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { selectContentState, useEditorStore, type ContentState } from '../store/editorStore';
import {
  deserializeLessonFromObjectLenient,
  serializeLessonToObject,
  type SerializedLesson
} from '../store/serialization';
import {
  isValidNarrationManifest,
  type DeleteEligibility,
  type NarrationManifest,
  type PublishState,
} from '@course-authoring/shared-types';
import { fetchWithAuthRetry, normalizeApiError, parseApiError, type ApiError } from './apiUtils';
import { lessonLibraryKeys } from './courseRecordApi';
import { questionKeys } from './questionApi';
import { lessonDetailsQueryRoot } from './lessonLocaleCoverageQueries';

/**
 * API base URL - should be configured via environment variable
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Query key factory for lesson-related queries
 */
export const lessonKeys = {
  all: ['lessons'] as const,
  lists: () => [...lessonKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...lessonKeys.lists(), filters] as const,
  details: () => [...lessonDetailsQueryRoot] as const,
  // accountId is part of the key so base and client-context reads never collide
  // in the cache ('base' sentinel for Standard context).
  detail: (id: string, locale = 'en', accountId: number | null = null) =>
    [...lessonKeys.details(), id, locale, accountId ?? 'base'] as const,
};

/**
 * API Error type for consistent error handling
 */
export type { ApiError } from './apiUtils';

export interface CreateLessonInput {
  title: string;
  description?: string;
}

export interface DuplicateLessonResult {
  id: string;
  title: string;
}

/**
 * Fetch lesson data from the API
 * Requirements: 4.5
 */
export type LessonEditorData = ContentState & {
  /** NA-190: the active context's publish state — kept out of the seeded editor content. */
  publishState: PublishState | null;
  /** NA-283: server-computed Delete availability behind the top-bar action. */
  deleteEligibility: DeleteEligibility | null;
  /** Derived learner runtime data; excluded from editor ContentState serialization. */
  narration?: NarrationManifest;
};

/**
 * NA-296: every lesson save mutation carries the cached `publishState` forward
 * rather than refetching it (deliberate — see the callers), so `editable`/
 * `lockedByEmtrainEditing` go briefly stale after a save. `canCancelEdit` is
 * the one field where staleness is user-visible on literally the first edit:
 * the spec requires Cancel disappear the instant an edit lands, not on the
 * next unrelated refetch. This is a known, one-way transition (a successful
 * save while editing always means the session is no longer cancelable) —
 * not re-deriving eligibility client-side, just not lying with a stale copy.
 */
export function withEditedPublishState(previous: LessonEditorData | undefined): PublishState | null {
  return previous?.publishState
    ? { ...previous.publishState, canCancelEdit: false }
    : previous?.publishState ?? null;
}

async function fetchLesson(lessonId: string, locale = 'en', accountId: number | null = null): Promise<LessonEditorData> {
  const params = new URLSearchParams({ locale });
  if (accountId !== null) params.set('accountId', String(accountId));
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}?${params}`);

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to fetch lesson');
  }

  const data = await response.json();
  // Use lenient deserialization to allow loading lessons with invalid blocks
  // Users can then fix validation issues in the editor
  const result = deserializeLessonFromObjectLenient(data);

  if (!result.success) {
    // Only throw on structural errors (missing required fields like id, type)
    // Not on content validation errors (invalid URLs, missing captions)
    const error: ApiError = {
      status: 422,
      code: 'VALIDATION_ERROR',
      message: result.errors.map(e => e.message).join(', '),
      retryable: false,
    };
    throw error;
  }

  const publishState = data && typeof data === 'object' && 'publishState' in data
    ? (data as { publishState: PublishState | null }).publishState ?? null
    : null;
  const deleteEligibility = data && typeof data === 'object' && 'deleteEligibility' in data
    ? (data as { deleteEligibility: DeleteEligibility | null }).deleteEligibility ?? null
    : null;
  const narration = data && typeof data === 'object' && 'narration' in data
    && isValidNarrationManifest((data as { narration?: unknown }).narration)
    ? (data as { narration: NarrationManifest }).narration
    : undefined;
  return { ...result.data, publishState, deleteEligibility, ...(narration ? { narration } : {}) };
}

export interface SaveLessonResponse {
  serialized: SerializedLesson;
  idMappings: Record<string, string>;
}

/**
 * Save lesson data to the API
 * Requirements: 4.2
 */
async function saveLesson(lessonId: string, content: ContentState, locale = 'en', accountId: number | null = null): Promise<SaveLessonResponse> {
  const serialized = serializeLessonToObject(content);
  // The backend reads the client-context account from lessonMeta.accountId.
  if (accountId !== null) {
    serialized.lessonMeta = { ...serialized.lessonMeta, accountId } as SerializedLesson['lessonMeta'];
  }

  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}?${new URLSearchParams({ locale })}`, {
    method: 'PUT',
    body: JSON.stringify(serialized),
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to save lesson');
  }

  const body = await response.json();
  const idMappings: Record<string, string> =
    body && typeof body.idMappings === 'object' && body.idMappings !== null
      ? body.idMappings
      : {};

  return {
    serialized: body as SerializedLesson,
    idMappings,
  };
}

/**
 * Create a new lesson
 */
async function createLesson(input: CreateLessonInput): Promise<{ id: string; title: string }> {
  const title = input.title.trim() || 'Untitled Lesson';
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons`, {
    method: 'POST',
    body: JSON.stringify({ title }),
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to create lesson');
  }

  return response.json();
}

/**
 * Duplicate an existing lesson
 */
async function duplicateLesson(lessonId: string): Promise<DuplicateLessonResult> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}/duplicate`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to duplicate lesson');
  }

  return response.json();
}

/**
 * Delete a lesson
 */
async function deleteLesson(lessonId: string): Promise<void> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to delete lesson');
  }
}

/**
 * Hook for fetching lesson data
 * Requirements: 4.5
 *
 * Fetches lesson data via TanStack Query and seeds the Editor Store.
 *
 * @param lessonId - The ID of the lesson to fetch
 * @param options - Additional query options
 */
export function useLessonQuery(
  lessonId: string,
  options?: {
    enabled?: boolean;
    locale?: string;
    accountId?: number | null;
    onSuccess?: (data: ContentState) => void;
  }
) {
  const locale = options?.locale ?? 'en';
  const accountId = options?.accountId ?? null;
  return useQuery({
    queryKey: lessonKeys.detail(lessonId, locale, accountId),
    queryFn: () => fetchLesson(lessonId, locale, accountId),
    enabled: options?.enabled ?? !!lessonId,
    placeholderData: undefined,
    // Always revalidate lesson content when (re)observed. The global default is
    // 5 minutes, but base edits ripple into client-context views, so switching
    // context must refetch rather than serve a fresh-by-staleTime cached copy.
    // The editor store is the source of truth for in-progress edits; the seed
    // hook applies refetched data only when there are no unsaved edits.
    staleTime: 0,
    retry: (failureCount, error: unknown) => {
      // Only retry if the error is retryable and we haven't exceeded max retries
      if (error && typeof error === 'object' && 'retryable' in error) {
        return Boolean((error as ApiError).retryable) && failureCount < 3;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook for saving lesson data
 * Requirements: 4.2
 *
 * Provides mutation for persisting lesson data to the server.
 *
 * @param lessonId - The ID of the lesson to save
 * @param options - Mutation callbacks
 */
export function useLessonMutation(
  lessonId: string,
  options?: {
    locale?: string;
    accountId?: number | null;
    onSuccess?: (postReconcileContent: ContentState) => void;
    onError?: (error: ApiError) => void;
  }
) {
  const queryClient = useQueryClient();
  const locale = options?.locale ?? 'en';
  const accountId = options?.accountId ?? null;

  return useMutation({
    mutationFn: (content: ContentState) => saveLesson(lessonId, content, locale, accountId),
    onSuccess: (response, savedContent) => {
      // Reconcile any temp_ block ids -> server-assigned ids BEFORE writing to
      // the query cache; otherwise the cache would hold ghosts of temp ids.
      // (Client context produces no new blocks, so no idMappings there.)
      let postContent = savedContent;
      if (Object.keys(response.idMappings).length > 0) {
        useEditorStore.getState().reconcileBlockIds(response.idMappings);
        postContent = selectContentState(useEditorStore.getState());
      }
      // The cache entry is LessonEditorData, but postContent is bare saved
      // ContentState — carry publishState (and NA-283 eligibility) forward or
      // every autosave blanks the lifecycle pill/actions until the next
      // refetch. canCancelEdit is forced false (NA-296) since this save just
      // made the session no longer cancelable, whatever else stayed stale.
      queryClient.setQueryData<LessonEditorData>(
        lessonKeys.detail(lessonId, locale, accountId),
        (previous) => ({
          ...postContent,
          publishState: withEditedPublishState(previous),
          deleteEligibility: previous?.deleteEligibility ?? null,
        }),
      );
      // Narration is server-derived from the persisted content and SpeechAudio
      // rows. Refetch it after a save instead of retaining stale runtime data.
      void queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(lessonId, locale, accountId),
      });
      // Inheritance is one-way: base/Standard content ripples into every
      // client-context view of this lesson (non-customized blocks + metadata),
      // but client-context edits are account-scoped and never affect base or
      // other clients. So only a base save needs to refresh the OTHER cached
      // variants for this lessonId; a client save just updates its own entry
      // (seeded above).
      //
      // Use removeQueries (not invalidate) for the inactive sibling variants:
      // the editor store is seeded once per context via useSeedLessonContent, and
      // a merely-stale-but-present cache would let the seed hook grab the stale
      // copy on switch and then ignore the background refetch. Dropping the cache
      // forces a clean fetch + fresh seed when the user switches into a client
      // context. `type: 'inactive'` leaves the active editor's own query (seeded
      // above) untouched.
      if (accountId === null) {
        queryClient.removeQueries({
          queryKey: [...lessonKeys.details(), lessonId],
          type: 'inactive',
        });
      }
      // A saved lesson's blocks may have added, removed, or otherwise changed
      // which questions it references — invalidate question-usage data (both
      // the per-question and bulk queries share this key prefix) so the
      // Question Bank screen's "Used in" counts refresh instead of staying
      // stale for up to the default 5-minute staleTime.
      void queryClient.invalidateQueries({ queryKey: [...questionKeys.all, 'usage'] });
      options?.onSuccess?.(postContent);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
    retry: (failureCount, error: unknown) => {
      // Only retry if the error is retryable
      if (error && typeof error === 'object' && 'retryable' in error) {
        return Boolean((error as ApiError).retryable) && failureCount < 3;
      }
      return false;
    },
  });
}

/**
 * Hook for creating a new lesson
 */
export function useCreateLessonMutation(options?: {
  onSuccess?: (data: { id: string; title: string }) => void;
  onError?: (error: ApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLessonInput) => createLesson(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lessonLibraryKeys.lists() });
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}

/**
 * Hook for duplicating a lesson
 */
export function useDuplicateLessonMutation(options?: {
  onSuccess?: (data: DuplicateLessonResult) => void;
  onError?: (error: ApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lessonId: string) => duplicateLesson(lessonId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lessonLibraryKeys.lists() });
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}

/**
 * Hook for deleting a lesson
 */
export function useDeleteLessonMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lessonId: string) => deleteLesson(lessonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lessonLibraryKeys.lists() });
      options?.onSuccess?.();
    },
    onError: (error: unknown, lessonId: string) => {
      const apiError = normalizeApiError(error, 'Failed to delete lesson');
      // NA-283: a refusal means the caller rendered against stale eligibility.
      // Refresh BOTH surfaces that gate on it — the lists' rows and the detail
      // read behind the editor top bar — so Delete comes back disabled with its
      // reason rather than inviting the author to try again.
      if (apiError.status === 409) {
        queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
        queryClient.invalidateQueries({ queryKey: lessonLibraryKeys.lists() });
        queryClient.invalidateQueries({ queryKey: [...lessonKeys.details(), lessonId] });
      }
      options?.onError?.(apiError);
    },
  });
}
